import React, { useState, useEffect } from 'react';
import { ArrowUpDown, TrendingUp, TrendingDown } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, XAxis } from 'recharts';
import { formatInTimeZone } from 'date-fns-tz';
import { ET_TIMEZONE } from './utils/marketUtils';

const formatTime = (timestamp) => {
  if (!timestamp || isNaN(timestamp)) return '';
  return formatInTimeZone(new Date(timestamp), ET_TIMEZONE, 'HH:mm:ss');
};

const getArrowColor = (arrow, avgChangePercent) => {
  if (!arrow || arrow === '') return 'inherit';
  
  if (avgChangePercent >= 0) {
    switch(arrow) {
      case '↗': return '#166534'; // Dark green - positive & accelerating
      case '→': return '#22c55e'; // Green - positive & steady
      case '↘': return '#f59e0b'; // Orange - positive but decelerating
      default: return 'inherit';
    }
  } else {
    switch(arrow) {
      case '↗': return '#f97316'; // Orange - negative but improving
      case '→': return '#ef4444'; // Red - negative & steady
      case '↘': return '#dc2626'; // Dark red - negative & getting worse
      default: return 'inherit';
    }
  }
};

const getUniqueTicks = (data, interval) => {
  const seenIntervals = new Set();
  return data.filter(item => {
    const intervalStart = Math.floor(item.timestamp / interval) * interval;
    if (!seenIntervals.has(intervalStart)) {
      seenIntervals.add(intervalStart);
      return true;
    }
    return false;
  }).map(item => item.timestamp);
};

const cleanStockName = (name) => {
  const suffixesToRemove = [
    'Common Stock', 'Common Shares', 'Common Subordinate Voting Shares',
    'Class A Common Stock', 'Class B Common Stock', 'Class C Common Stock',
    'Class A', 'Class B', 'Class C', 'American Depositary Shares', 'ADS',
    'Inc', 'Inc.', 'Ltd.', 'Ordinary Share', 'Ordinary Shares',
    'IncOrdinary Shares', 'Subordinate Voting Shares',
    'Units representing Partner Interests',
    'Common Units representing Partner Interests',
    'Corporation', 'Corp.', 'Limited', ', plcOrdinary Shares', ',.', 
    'AGOrdinary Shares', 'DIOrdinary Shares', 'TechOrdinary Shares',
    'HoldingOrdinary Shares', 'plc', 'AG', 'DI', 'Tech', 'Holding'
  ];
  
  let cleanedName = name;
  suffixesToRemove.forEach(suffix => {
    cleanedName = cleanedName.replace(new RegExp(`\\s*\\b${suffix}\\b\\s*`, 'gi'), '');
  });
  
  cleanedName = cleanedName.replace(/,\s*$/, '');
  cleanedName = cleanedName.replace(/\s+/g, ' ');
  
  return cleanedName.trim();
};

const calculateDailyGain = (currentPrice, previousClose) => {
  return ((currentPrice - previousClose) / previousClose) * 100;
};

const CustomTooltip = ({ active, payload, label, previousClose }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const dailyGain = calculateDailyGain(data.price, previousClose);
    return (
      <div style={{ backgroundColor: 'white', padding: '10px', border: '1px solid #ccc' }}>
        <p style={{ color: dailyGain >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
          {`Daily Gain: ${dailyGain >= 0 ? '+' : ''}${dailyGain.toFixed(2)}%`}
        </p>
        <p>{`Price: $${data.price.toFixed(2)}`}</p>
        <p>{`Time: ${formatTime(label)}`}</p>
      </div>
    );
  }
  return null;
};

export const StockTable = ({
  stocks,
  sortCriteria = [],
  onSort,
  marketStatus,
  priceHistoryMinutes = 90,
  pricePointIntervalSeconds = 30,
  xAxisTickIntervalMinutes = 5,
  avgChangeHistory = {},
  avgChangeByConfig = {},
  selectedConfig = { key: '24/8' },
  getAccelerationArrow,
  calculateStockScore,
  calculatePersonalizedAdjustment,
  userProfile,
  // Used for the Score column's AI-personalization tooltip / adjustment
  aiAnthropicResult,
  aiOpenaiResult,
  marketSentiment,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Phase 2: Helper function to find AI result for a stock symbol
  const getAiResultForStock = (symbol) => {
    // Check Claude results first, then OpenAI
    const claudePick = aiAnthropicResult?.topPicks?.find(p => p.symbol === symbol);
    const gptPick = aiOpenaiResult?.topPicks?.find(p => p.symbol === symbol);

    // Prefer Claude if both have results, or return whichever is available
    if (claudePick?.matchesUserSuccessPattern || claudePick?.matchesUserAvoidPattern) {
      return claudePick;
    }
    if (gptPick?.matchesUserSuccessPattern || gptPick?.matchesUserAvoidPattern) {
      return gptPick;
    }
    // Return any pick if no pattern match
    return claudePick || gptPick || null;
  };

  // Find sort priority for a field (1, 2, 3 or null)
  const getSortPriority = (field) => {
    const index = sortCriteria.findIndex(c => c.field === field);
    return index >= 0 ? index + 1 : null;
  };

  // Get sort order for a field
  const getSortOrder = (field) => {
    const criterion = sortCriteria.find(c => c.field === field);
    return criterion ? criterion.order : null;
  };

  // Get header background color based on sort priority
  const getHeaderStyle = (field) => {
    const priority = getSortPriority(field);
    const baseStyle = {
      textAlign: 'right',
      padding: '0.5rem',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    };

    if (!priority) {
      return { ...baseStyle, backgroundColor: 'transparent' };
    }

    // Different shades for different priorities
    const colors = {
      1: '#dbeafe', // Primary sort - light blue
      2: '#e0f2fe', // Secondary sort - lighter blue
      3: '#f0f9ff'  // Tertiary sort - very light blue
    };

    return {
      ...baseStyle,
      backgroundColor: colors[priority] || 'transparent',
      borderBottom: priority === 1 ? '2px solid #3b82f6' : '1px solid #93c5fd'
    };
  };

  // Render sort icon with priority number
  const renderSortIcon = (field) => {
    const priority = getSortPriority(field);
    const order = getSortOrder(field);

    if (!priority) return <ArrowUpDown size={16} style={{ opacity: 0.4 }} />;

    const priorityBadge = sortCriteria.length > 1 ? (
      <span style={{
        fontSize: '10px',
        marginLeft: '4px',
        backgroundColor: priority === 1 ? '#2563eb' : '#60a5fa',
        color: 'white',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold'
      }}>
        {priority}
      </span>
    ) : null;

    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {order === 'asc' ? (
          <TrendingUp size={18} style={{ color: '#2563eb' }} />
        ) : (
          <TrendingDown size={18} style={{ color: '#2563eb' }} />
        )}
        {priorityBadge}
      </span>
    );
  };

  // Handle click with shift detection
  const handleHeaderClick = (field, event) => {
    onSort(field, event.shiftKey);
  };

  const isMarketOpen = marketStatus && (marketStatus.market === 'open' || marketStatus.market === 'extended-hours');

  const filterRecentPriceHistory = (priceHistory) => {
    const currentTime = Date.now();
    return priceHistory.filter(point => (currentTime - point.timestamp) <= priceHistoryMinutes * 60 * 1000);
  };

  const renderMobileTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '3%' }}>Symbol</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '94%' }}>Chart</th>
          <th
            style={{ ...getHeaderStyle('avgChangePercent'), width: '3%' }}
            onClick={(e) => handleHeaderClick('avgChangePercent', e)}
            title="Click to sort, Shift+Click to add secondary sort"
          >
            Avg % {renderSortIcon('avgChangePercent')}
          </th>
        </tr>
      </thead>
      <tbody>
        {stocks.map((stock) => (
          <tr key={stock.symbol} style={{ borderBottom: '1px solid #e5e7eb' }}>
            <td style={{ padding: '0.5rem', verticalAlign: 'top', fontSize: '0.8rem' }}>
              <div>{stock.symbol}</div>
            </td>
            <td style={{ padding: '0.5rem' }}>
              {stock.priceHistory && stock.priceHistory.length > 0 ? (
                <div style={{ width: '100%', height: '100px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filterRecentPriceHistory(stock.priceHistory)}>
                      <Line type="linear" dataKey="price" stroke={stock.avgChangePercent >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={2} dot={false} />
                      <YAxis domain={['dataMin', 'dataMax']} hide />
                      <XAxis dataKey="timestamp" tickFormatter={formatTime} hide />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', backgroundColor: '#f3f4f6', fontSize: '0.8rem' }}>
                  {!isMarketOpen ? 'Market closed. ' : ''}Collecting data...
                </div>
              )}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top', fontSize: '0.8rem' }}>
              <div style={{ color: stock.avgChangePercent >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stock.avgChangePercent >= 0 ? '+' : ''}{stock.avgChangePercent.toFixed(2)}%
                {getAccelerationArrow && (() => {
                  const arrow = getAccelerationArrow(avgChangeHistory[stock.symbol]);
                  return arrow ? (
                    <span style={{ 
                      marginLeft: '6px', 
                      fontSize: '1.5em',
                      fontWeight: 'bold',
                      color: getArrowColor(arrow, stock.avgChangePercent)
                    }}>
                      {arrow}
                    </span>
                  ) : null;
                })()}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderDesktopTable = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '12%' }}>Symbol / Name</th>
          <th style={{ textAlign: 'left', padding: '0.5rem', width: '75%' }}>Recent Price Chart</th>
          <th style={getHeaderStyle('score')} onClick={(e) => handleHeaderClick('score', e)} title="Click to sort, Shift+Click to add secondary sort">
            Score {renderSortIcon('score')}
          </th>
          <th style={getHeaderStyle('avgChangePercent')} onClick={(e) => handleHeaderClick('avgChangePercent', e)} title="Click to sort, Shift+Click to add secondary sort">
            Avg Change % {renderSortIcon('avgChangePercent')}
          </th>
          <th style={getHeaderStyle('dailyGainPercentage')} onClick={(e) => handleHeaderClick('dailyGainPercentage', e)} title="Click to sort, Shift+Click to add secondary sort">
            Daily Gain % {renderSortIcon('dailyGainPercentage')}
          </th>
        </tr>
      </thead>
      <tbody>
        {stocks.map((stock) => {
          // Get config-specific avgChange for this stock
          const stockAvgChange = avgChangeByConfig[stock.symbol]?.[selectedConfig.key] ?? stock.avgChangePercent ?? 0;

          return (
          <tr key={stock.symbol} style={{ borderBottom: '1px solid #e5e7eb' }}>
            {/* Symbol + Name + Sector merged — stacked vertically saves
                ~5% horizontal space for the Recent Price Chart column. */}
            <td style={{ padding: '0.5rem', width: '12%', verticalAlign: 'top' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                {stock.dailyGainPercentage >= 0 ? (
                  <TrendingUp size={14} style={{ color: '#22c55e', marginTop: '4px', flexShrink: 0 }} />
                ) : (
                  <TrendingDown size={14} style={{ color: '#ef4444', marginTop: '4px', flexShrink: 0 }} />
                )}
                <div style={{
                  width: '1.75rem',
                  height: '1.75rem',
                  backgroundColor: stock.dailyGainPercentage >= 0 ? '#22c55e' : '#ef4444',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: '600',
                  flexShrink: 0,
                }}>
                  {stock.symbol.slice(0, 1)}
                </div>
                <div style={{ minWidth: 0, lineHeight: '1.25' }}>
                  <div style={{ fontWeight: '600', fontSize: '0.95em' }}>{stock.symbol}</div>
                  <div style={{
                    fontSize: '0.8em',
                    color: '#4b5563',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                  }}>
                    {cleanStockName(stock.name)}
                  </div>
                  {stock.sicDescription && (
                    <div style={{
                      fontSize: '0.7em',
                      color: '#9ca3af',
                      marginTop: '2px',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                    }}>
                      {stock.sicDescription}
                    </div>
                  )}
                </div>
              </div>
            </td>
            <td style={{ padding: '0.5rem', width: '75%' }}>
              {stock.priceHistory && stock.priceHistory.length > 0 ? (
                <div style={{ width: '100%', height: '200px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={filterRecentPriceHistory(stock.priceHistory)}>
                      <Line type="linear" dataKey="price" stroke={stockAvgChange >= 0 ? "#22c55e" : "#ef4444"} strokeWidth={2} dot={false} />
                      <YAxis 
                        domain={['dataMin', 'dataMax']} 
                        hide={false} 
                        padding={{ top: 10, bottom: 10 }} 
                      />
                      <XAxis 
                        dataKey="timestamp" 
                        tickFormatter={formatTime}
                        type="number"
                        domain={['dataMin', 'dataMax']}
                        ticks={getUniqueTicks(filterRecentPriceHistory(stock.priceHistory), xAxisTickIntervalMinutes * 60 * 1000)}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip content={<CustomTooltip previousClose={stock.previousClose} />} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', backgroundColor: '#f3f4f6' }}>
                  {!isMarketOpen ? 'Market is currently closed. ' : ''}Collecting price data...
                </div>
              )}
            </td>
            {/* Score column */}
            <td style={{ padding: '0.5rem', textAlign: 'center', verticalAlign: 'top' }}>
              {calculateStockScore && (() => {
                const arrow = getAccelerationArrow ? getAccelerationArrow(avgChangeHistory[stock.symbol]) : '';
                const dailyGain = stock.dailyGainPercentage || 0;
                const scoreData = calculateStockScore(dailyGain, stockAvgChange, arrow);

                // Calculate individual points for tooltip
                const dailyGainPoints = dailyGain >= 10 ? 3 : dailyGain >= 5 ? 2 : dailyGain >= 0 ? 1 : 0;
                const avgChangePoints = stockAvgChange >= 2 ? 3 : stockAvgChange >= 1 ? 2 : stockAvgChange >= 0 ? 1 : 0;
                const accelPoints = arrow === '↗' ? 2 : arrow === '→' ? 1 : 0;

                // Phase 2: Learning Loop - Get AI result for this stock
                const aiResult = getAiResultForStock(stock.symbol);

                // Calculate personalized adjustment with AI result
                const personalization = calculatePersonalizedAdjustment
                  ? calculatePersonalizedAdjustment(userProfile, marketSentiment, aiResult)
                  : { adjustment: 0, reasons: [], hasPositiveMatch: false, hasNegativeMatch: false, matchedPattern: null, isSuccessPattern: false, isAvoidPattern: false };

                const finalPoints = Math.max(0, Math.min(11, scoreData.points + personalization.adjustment));

                // Get grade for final points (extended to handle 0-11)
                const getGrade = (pts) => {
                  if (pts >= 8) return { grade: 'A+', emoji: '🟢🟢', color: '#15803d' };
                  if (pts >= 6) return { grade: 'A', emoji: '🟢', color: '#22c55e' };
                  if (pts >= 4) return { grade: 'B', emoji: '🟡', color: '#eab308' };
                  if (pts >= 2) return { grade: 'C', emoji: '🟠', color: '#f97316' };
                  return { grade: 'D', emoji: '🔴', color: '#ef4444' };
                };

                const finalGrade = getGrade(finalPoints);

                // Build tooltip text
                let tooltipText = `Score: ${finalGrade.grade} (${personalization.adjustment !== 0 ? `${scoreData.points}${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}=${finalPoints}` : `${scoreData.points}/8`})\n` +
                  `─────────────────\n` +
                  `Base Score: ${scoreData.points}/8\n` +
                  `  Daily Gain: ${dailyGain >= 0 ? '+' : ''}${dailyGain.toFixed(2)}% → ${dailyGainPoints}pt\n` +
                  `  Avg Change: ${stockAvgChange >= 0 ? '+' : ''}${stockAvgChange.toFixed(2)}% → ${avgChangePoints}pt\n` +
                  `  Acceleration: ${arrow || 'N/A'} → ${accelPoints}pt`;

                if (personalization.adjustment !== 0) {
                  tooltipText += `\n─────────────────\n` +
                    `Personalization: ${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}\n`;
                  personalization.reasons.forEach(r => {
                    tooltipText += `  ${r.type === 'positive' ? '✨' : '⚠️'} ${r.text}\n`;
                  });
                  tooltipText += `─────────────────\n` +
                    `Final Score: ${finalPoints}`;
                }

                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      cursor: 'help'
                    }}
                    title={tooltipText}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <span style={{ fontSize: '1.2em' }}>{finalGrade.emoji}</span>
                      {/* Phase 2: Personalization icons based on AI pattern match */}
                      {personalization.isSuccessPattern && (
                        <span style={{ fontSize: '0.8em' }}>✨</span>
                      )}
                      {personalization.isAvoidPattern && (
                        <span style={{ fontSize: '0.8em' }}>⚠️</span>
                      )}
                    </div>
                    <span style={{
                      fontWeight: 'bold',
                      color: finalGrade.color,
                      fontSize: '0.9em'
                    }}>
                      {finalGrade.grade}
                    </span>
                    <span style={{
                      fontSize: '0.7em',
                      color: '#6b7280'
                    }}>
                      ({personalization.adjustment !== 0 ? `${scoreData.points}${personalization.adjustment > 0 ? '+' : ''}${personalization.adjustment}` : `${scoreData.points}/8`})
                    </span>
                  </div>
                );
              })()}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top' }}>
              <div style={{ color: stockAvgChange >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stockAvgChange >= 0 ? '+' : ''}{stockAvgChange.toFixed(2)}%
                {getAccelerationArrow && (() => {
                  const arrow = getAccelerationArrow(avgChangeHistory[stock.symbol]);
                  return arrow ? (
                    <span style={{
                      marginLeft: '4px',
                      fontSize: '0.9em',
                      color: getArrowColor(arrow, stockAvgChange)
                    }}>
                      {arrow}
                    </span>
                  ) : null;
                })()}
              </div>
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', verticalAlign: 'top' }}>
              <div style={{ color: stock.dailyGainPercentage >= 0 ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                {stock.dailyGainPercentage >= 0 ? '+' : ''}{stock.dailyGainPercentage.toFixed(2)}%
              </div>
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );

  return isMobile ? renderMobileTable() : renderDesktopTable();
};

export default StockTable;
